import React, { useState, useMemo } from 'react';
import { ExternalLink, Pencil, Trash2, Plus } from 'lucide-react';
import { Alert } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { InlineSpinner } from '@/components/ui/inline-spinner';
import type { AuthUserDto, CreateResourceLinkRequest, ResourceLinkDto } from '@signalvote/shared';
import {
  useResources,
  useCreateResource,
  useUpdateResource,
  useDeleteResource,
} from '../hooks/queries';

interface ResourceLinksSectionProps {
  user?: AuthUserDto;
  isAdmin?: boolean;
}

const ResourceLinksSection: React.FC<ResourceLinksSectionProps> = ({ isAdmin }) => {
  // TanStack Query hooks
  const { data: resources = [], isLoading, error } = useResources();
  const createMutation = useCreateResource();
  const updateMutation = useUpdateResource();
  const deleteMutation = useDeleteResource();

  // Local UI state
  const [editDialog, setEditDialog] = useState(false);
  const [editingLink, setEditingLink] = useState<ResourceLinkDto | null>(null);
  const [formData, setFormData] = useState<CreateResourceLinkRequest>({
    title: '',
    url: '',
    description: '',
    category: 'VoterResources',
    displayOrder: 0,
    isActive: true
  });

  // Derive categorized resources from query data
  const { voterResources, campaignInfo, generalResources } = useMemo(() => {
    const voter = resources.filter(
      (link) => link.category === 'VoterResources' && link.isActive
    );
    const campaign = resources.filter(
      (link) => link.category === 'CampaignInformation' && link.isActive
    );
    const general = resources.filter(
      (link) => link.category === 'GeneralResources' && link.isActive
    );
    return { voterResources: voter, campaignInfo: campaign, generalResources: general };
  }, [resources]);

  const handleAddNew = (category: string) => {
    setEditingLink(null);
    setFormData({
      title: '',
      url: '',
      description: '',
      category,
      displayOrder: 0,
      isActive: true
    });
    setEditDialog(true);
  };

  const handleEdit = (link: ResourceLinkDto) => {
    setEditingLink(link);
    setFormData({
      title: link.title,
      url: link.url,
      description: link.description || '',
      category: link.category,
      displayOrder: link.displayOrder,
      isActive: link.isActive
    });
    setEditDialog(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.url) {
      return;
    }

    try {
      if (editingLink) {
        await updateMutation.mutateAsync({
          id: editingLink.id,
          updates: formData
        });
      } else {
        await createMutation.mutateAsync(formData);
      }
      setEditDialog(false);
    } catch (err) {
      console.error('Error saving resource link:', err);
    }
  };

  const handleDelete = async (link: ResourceLinkDto) => {
    if (!window.confirm(`Are you sure you want to delete "${link.title}"?`)) {
      return;
    }

    try {
      await deleteMutation.mutateAsync(link.id);
    } catch (err) {
      console.error('Error deleting resource link:', err);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const renderResourceCategory = (
    title: string,
    categoryResources: ResourceLinkDto[],
    category: string
  ) => (
    <Card className="mb-6">
      <CardContent className="pt-6">
        <div className="mb-4 flex items-center justify-between">
          <h6 className="text-lg font-semibold">{title}</h6>
          {isAdmin && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-primary"
              onClick={() => handleAddNew(category)}
              title="Add new link"
            >
              <Plus className="h-4 w-4" />
            </Button>
          )}
        </div>

        {categoryResources.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No resources available
          </p>
        ) : (
          <ul className="list-none p-0">
            {categoryResources.map((resource, index) => (
              <React.Fragment key={resource.id}>
                {index > 0 && <Separator />}
                <li
                  className={`flex items-center justify-between gap-2 py-2 ${
                    isAdmin ? 'hover:bg-accent' : ''
                  }`}
                >
                  <div className="min-w-0">
                    <a
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      {resource.title}
                      <ExternalLink className="h-4 w-4" />
                    </a>
                    {resource.description && (
                      <p className="text-sm text-muted-foreground">
                        {resource.description}
                      </p>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="flex shrink-0 items-center">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleEdit(resource)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleDelete(resource)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </li>
              </React.Fragment>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <InlineSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <Alert severity="error" className="mb-6">
        {error instanceof Error ? error.message : 'Failed to load resource links'}
      </Alert>
    );
  }

  return (
    <>
      <div className="mb-8">
        {renderResourceCategory('Voter Resources', voterResources, 'VoterResources')}
        {renderResourceCategory('Campaign Information', campaignInfo, 'CampaignInformation')}
        {renderResourceCategory('General Resources', generalResources, 'GeneralResources')}
      </div>

      {/* Edit Dialog */}
      <Dialog
        open={editDialog}
        onOpenChange={(open) => {
          if (!open) setEditDialog(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingLink ? 'Edit Resource Link' : 'Add Resource Link'}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 pt-2">
            <div className="space-y-1">
              <Label htmlFor="resource-title">Title</Label>
              <Input
                id="resource-title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="resource-url">URL</Label>
              <Input
                id="resource-url"
                type="url"
                placeholder="https://example.com"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="resource-description">Description (optional)</Label>
              <textarea
                id="resource-description"
                rows={2}
                className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                value={formData.description ?? ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="resource-category">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger id="resource-category">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="VoterResources">Voter Resources</SelectItem>
                  <SelectItem value="CampaignInformation">Campaign Information</SelectItem>
                  <SelectItem value="GeneralResources">General Resources</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="resource-display-order">Display Order</Label>
              <Input
                id="resource-display-order"
                type="number"
                value={formData.displayOrder ?? 0}
                onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setEditDialog(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving || !formData.title || !formData.url}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ResourceLinksSection;
